import React, { useMemo } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { limit } from 'firebase/firestore';
import { Card, Table, Badge, CsvExportButton } from '../../components/UI';
import { useFirestoreCollection } from '../../hooks';
import { STORE_PLANS } from '../../constants/plans';
import styles from './Plans.module.css';

// Plans are in-app purchase products created in the store consoles, NOT rows in
// Firestore. This page used to read a `subscriptionPlans` collection that has
// never held a document, so the tab was permanently empty and its editor wrote
// records nothing consumed.
//
// What it shows instead: the tier catalogue the app actually ships
// (src/constants/subscriptionConfig.js, mirrored into src/constants/plans.js),
// the App Store / Play product id for each billing cycle, and how many users
// are currently on each tier. Pricing is read-only here on purpose — the money
// is set in Google Play Console and App Store Connect, and StoreKit / Play
// Billing quote the live price at purchase time.

const ACCOUNT_TYPE_VARIANT = {
  individual: 'primary',
  business: 'secondary',
  veterinary: 'tertiary',
};

const formatMoney = (amount, currency = 'EUR') => {
  if (amount == null) return '—';
  if (Number(amount) === 0) return 'Free';
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};

/** Yearly saving vs paying monthly for twelve months. */
const yearlySaving = (plan) => {
  if (!plan.monthly || !plan.yearly) return null;
  const monthlyTotal = plan.monthly * 12;
  if (monthlyTotal <= plan.yearly) return null;
  return Math.round(((monthlyTotal - plan.yearly) / monthlyTotal) * 100);
};

const Plans = () => {
  // Live subscriber counts. `subscriptionTier` is the entitlement the app reads,
  // and subscriptionEndDate is what actually decides whether it is still valid —
  // subscriptionStatus goes stale, so it is deliberately not used here.
  const userConstraints = useMemo(() => [limit(2000)], []);
  const { data: users, loading } = useFirestoreCollection('users', userConstraints);

  const counts = useMemo(() => {
    const now = Date.now();
    const active = new Map();
    const lapsed = new Map();
    users.forEach((u) => {
      const tier = u.subscriptionTier || 'paw_print';
      const endMs = u.subscriptionEndDate
        ? (u.subscriptionEndDate.toMillis
            ? u.subscriptionEndDate.toMillis()
            : new Date(u.subscriptionEndDate).getTime())
        : null;
      // A free tier never expires; a paid tier counts as active only while its
      // period is still running.
      const isActive = tier === 'paw_print' || !endMs || endMs > now;
      const bucket = isActive ? active : lapsed;
      bucket.set(tier, (bucket.get(tier) || 0) + 1);
    });
    return { active, lapsed };
  }, [users]);

  const rows = useMemo(
    () =>
      STORE_PLANS.map((p) => ({
        ...p,
        activeUsers: counts.active.get(p.id) || 0,
        lapsedUsers: counts.lapsed.get(p.id) || 0,
        savingPercent: yearlySaving(p),
      })),
    [counts]
  );

  const csvColumns = [
    { key: 'id', label: 'Tier' },
    { key: 'name', label: 'Name' },
    { key: 'accountType', label: 'Audience' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'yearly', label: 'Yearly' },
    { key: 'currency', label: 'Currency' },
    { key: 'storeProductIdMonthly', label: 'Store Product (monthly)' },
    { key: 'storeProductIdYearly', label: 'Store Product (yearly)' },
    { key: 'stripeProductId', label: 'Stripe Product' },
    { key: 'activeUsers', label: 'Active Subscribers' },
    { key: 'lapsedUsers', label: 'Lapsed' },
  ];

  const columns = [
    {
      key: 'name',
      title: 'Plan',
      render: (value, row) => (
        <div>
          <div className={styles.planName}>{value}</div>
          <code className={styles.planId}>{row.id}</code>
        </div>
      ),
    },
    {
      key: 'accountType',
      title: 'Audience',
      render: (v) =>
        v ? <Badge variant={ACCOUNT_TYPE_VARIANT[v] || 'inactive'}>{v}</Badge> : '—',
    },
    {
      key: 'monthly',
      title: 'Monthly',
      render: (v, row) => formatMoney(v, row.currency),
    },
    {
      key: 'yearly',
      title: 'Yearly',
      render: (v, row) => (
        <div>
          <div>{formatMoney(v, row.currency)}</div>
          {row.savingPercent != null && (
            <div className={styles.meta}>save {row.savingPercent}%</div>
          )}
        </div>
      ),
    },
    {
      key: 'storeProductIdMonthly',
      title: 'Store product IDs',
      render: (_, row) =>
        row.storeProductIdMonthly ? (
          <div className={styles.skuCell}>
            <code>{row.storeProductIdMonthly}</code>
            <code>{row.storeProductIdYearly}</code>
          </div>
        ) : (
          <span className={styles.meta}>free tier — no SKU</span>
        ),
    },
    {
      key: 'activeUsers',
      title: 'Subscribers',
      render: (v, row) => (
        <div>
          <div className={styles.planName}>{v}</div>
          {row.lapsedUsers > 0 && (
            <div className={styles.meta}>{row.lapsedUsers} lapsed</div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Plans</h1>
          <Badge variant="primary">{STORE_PLANS.length} tiers</Badge>
        </div>
        <p className={styles.subtitle}>
          Subscription tiers the app ships, with live subscriber counts
          {!loading && <span className={styles.liveText}> Live</span>}
        </p>
      </div>

      <Card>
        <div className={styles.notice}>
          <strong>Pricing is set in the store consoles, not here.</strong> These plans
          are in-app purchase products; StoreKit and Play Billing quote the live
          price at purchase time, so this tab is read-only. Edit them in{' '}
          <a
            href="https://play.google.com/console"
            target="_blank"
            rel="noreferrer"
          >
            Google Play Console → Monetize → Subscriptions <FiExternalLink />
          </a>{' '}
          and{' '}
          <a
            href="https://appstoreconnect.apple.com"
            target="_blank"
            rel="noreferrer"
          >
            App Store Connect → In-App Purchases <FiExternalLink />
          </a>
          . The figures below come from the app's own config and are what the UI
          falls back to when a live store price fails to load — if they disagree
          with the consoles, the app config is the thing to correct.
        </div>

        <div className={styles.toolbar}>
          <CsvExportButton rows={rows} columns={csvColumns} filename="dierend-plans" />
        </div>

        <Table columns={columns} data={rows} emptyMessage="No plans configured" />
      </Card>

      <Card>
        <div className={styles.notice}>
          <strong>Discount codes do not apply to these plans.</strong> Apple and
          Google own subscription billing, so a Dierend promo code cannot discount
          a plan and store rules forbid working around it. Use App Store{' '}
          <em>Offer Codes</em> and Play <em>promo codes</em> instead — both are
          created in the same consoles. The Discount Codes tab applies to
          Stripe-billed shop orders.
        </div>
      </Card>
    </div>
  );
};

export default Plans;
